import { QueryClientProvider } from '@tanstack/react-query'
import 'bootstrap/dist/css/bootstrap.min.css'
import { ChainPage } from 'pages/app/chain-page'
import Restaurant from 'pages/app/restaurant'
import University from 'pages/app/university'
import React, { ReactNode, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from './context/authentication/auth-context'
import { queryClient } from './services/queryClient'

import * as Sentry from '@sentry/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import 'chart.js/auto'
import { PreferencesProvider } from 'context/preferences/PreferencesContext'
import { ActivityPageV2 } from 'pages/app/activity/activityV2'
import { SingleCampaignPage } from 'pages/app/campaigns/single-campaign-page'
import { CompanyPage } from 'pages/app/company'
import { ContactsPage } from 'pages/app/contacts/contacts'
import { Dashboard } from 'pages/app/dashboard'
import HospitalPage from 'pages/app/hospital'
import NurseHomePage from 'pages/app/nursinghome'
import Failed from 'pages/auth/failed'
import { CommonLayout } from 'pages/common/layout'
import Privacy from 'pages/common/privacy'
import Terms from 'pages/common/terms'
import RebatePlatformAgreement from 'pages/common/rebates-platform-agreement'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { PageWithTitle } from 'utils/PageWithTitle'
import { APP_VERSION } from 'version'
import LoadingScreen from './components/Layout/LoadingScreen'
import { useAuth } from './context/authentication/useAuth'
import './main.css'
import { CampaignsPage } from './pages/app/campaigns/campaigns-page'
import { DealsPage } from './pages/app/deals/deals'
import K12DistrictPage from './pages/app/k12-district-page'
import { AppLayout } from './pages/app/layout'
import { OpportunitiesPage } from './pages/app/opportunities/opportunities'
import { AuthLayout } from './pages/auth/layout'
import Login from './pages/auth/login'
import Register from './pages/auth/register'
import ResetPassword from './pages/auth/reset-password'
import ResetPasswordConfirm from './pages/auth/reset-password-confirm'
import SSOLogin from './pages/auth/sso-login'
import SSOCallback from './pages/auth/sso-callback'
import Verified from './pages/auth/verified'
import NotFound from './pages/not-found'
import apiService from './services/api'
import { Rebates } from 'pages/app/rebates'
import { RebateOfferDetails } from './pages/app/rebates/rebate-offer-details'
import { featureFlagService } from './utils/featureFlagService'
import Oops from 'pages/oops'
import LodgingPage from 'pages/app/google_places/lodging'
import LodgingDoorPage from 'pages/app/google_places/lodgingDoor'
import OptOutForm from 'pages/common/opt-out-form'
import DataRequestForm from 'pages/common/data-request-form'
import DataRequestSuccess from 'pages/common/data-request-success'
import OptOutSuccess from 'pages/common/opt-out-success'
import SubscriptionAgreement from 'pages/common/subscription-agreement'
import ConveniencePage from 'pages/app/google_places/convenience'
import ConvenienceDoorPage from 'pages/app/google_places/convenienceDoor'
import { ImportPreviewsPage, ImportPreviewPage } from 'pages/app/importer-page'
import { SettingsLayout } from 'pages/app/settings/layout'
import { Settings } from 'pages/app/settings'
import { DoorProxyPage } from 'pages/app/door-proxy-page'
import IntercomMessenger from './components/IntercomMessenger'
import { SideMenuProvider } from 'features/settings/context/SideMenuContext'
import { SamplesDashboard } from 'pages/app/samples/SamplesDashboard'

if (import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: 'https://bdb286552bd38381f647c5dc31f4fa98@o4505942099230720.ingest.sentry.io/4505942563553280',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
    ],
    tracesSampleRate: 1,
    environment: import.meta.env.MODE,
    release: APP_VERSION,
  })
}

const sentryCreateBrowserRouter =
  import.meta.env.MODE === 'development'
    ? createBrowserRouter
    : Sentry.wrapCreateBrowserRouter(createBrowserRouter)

function Root() {
  const api = apiService()
  const { isAuthenticated, isCheckingAuth } = useAuth()

  // Common data shared across the app
  // We load it here so its available faster
  api.useGetActiveCompanyCategoriesAndTypes(!isAuthenticated)
  api.useGetUsers(!isAuthenticated)
  api.useGetSalesStages(!isAuthenticated)
  api.useGetTags('products', !isAuthenticated)
  api.useGetTags('contacts', !isAuthenticated)
  api.useGetProducts(!isAuthenticated)
  api.useGetCampaigns(undefined, !isAuthenticated)

  useEffect(() => {
    // If this is a production build, load the ga4 script
    if (process.env.NODE_ENV === 'production') {
      const src_script = document.createElement('script')
      src_script.src =
        'https://www.googletagmanager.com/gtag/js?id=G-2DXQWDV89Z'
      src_script.async = true

      const script = document.createElement('script')
      script.innerHTML =
        " window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-2DXQWDV89Z'); "

      document.head.appendChild(src_script)
      document.head.appendChild(script)

      return () => {
        document.head.removeChild(src_script)
        document.head.removeChild(script)
      }
    }
  }, [])

  if (isCheckingAuth) {
    return <LoadingScreen />
  }

  const appRouter = sentryCreateBrowserRouter([
    {
      path: '*',
      element: (
        <ProtectedRoute>
          <NotFound />
        </ProtectedRoute>
      ),
    },
    {
      path: '',
      element: (
        <ProtectedRoute>
          <RedirectRestaurants />
        </ProtectedRoute>
      ),
    },
    {
      path: '/',
      element: <AppLayout />,
      errorElement: <Oops />,
      children: [
        {
          path: '',
          element: (
            <ProtectedRoute>
              <RedirectRestaurants />
            </ProtectedRoute>
          ),
        },
        {
          path: 'dashboard',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Dashboard">
                <Dashboard />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'contacts/:type?/',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Contacts">
                <ContactsPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'activity/:type?/',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Activity">
                <ActivityPageV2 />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'company/:company_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Company">
                <CompanyPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'opportunities/',
          element: (
            <ProtectedRoute>
              <RedirectRestaurants />
            </ProtectedRoute>
          ),
        },
        {
          path: 'opportunities/:category/:sub_category/',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Opportunities">
                <OpportunitiesPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        // TODO determine url paths
        {
          path: 'restaurant/:firstbite_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Restaurant">
                <Restaurant />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'door/:door_proxy_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Door">
                <DoorProxyPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'chain/:chain_name',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Chain">
                <ChainPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'university/:university_name',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="University">
                <University />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'k12/:id/',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="K12 District">
                <K12DistrictPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'hospital/:hospital_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Hospital">
                <HospitalPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'lodging/chain/:lodging_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Lodging & Hospitality">
                <LodgingPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'lodging/door/:lodging_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Lodging & Hospitality">
                <LodgingDoorPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'convenience/chain/:convenience_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Convenience Store">
                <ConveniencePage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'convenience/door/:convenience_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Convenience Store">
                <ConvenienceDoorPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'nursinghome/:nursinghome_id',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Nursing Home">
                <NurseHomePage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'product',
          element: <Navigate to="/settings/products" replace />,
        },
        {
          path: 'settings/:group?/:tab?',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Settings">
                <SideMenuProvider>
                  <SettingsLayout />
                </SideMenuProvider>
              </PageWithTitle>
            </ProtectedRoute>
          ),
          children: [
            {
              path: '',
              element: <Settings />,
            },
          ],
        },
        {
          path: 'deals',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Deals">
                <DealsPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'campaigns',
          element: (
            <ProtectedRoute>
              <PageWithTitle title="Campaigns">
                <CampaignsPage />
              </PageWithTitle>
            </ProtectedRoute>
          ),
        },
        {
          path: 'samples/:type?/',
          element: (
            <ProtectedRoute>
              <FeatureProtectedRoute featureFlag="enableSamples">
                <PageWithTitle title="Samples">
                  <SamplesDashboard />
                </PageWithTitle>
              </FeatureProtectedRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: 'campaigns/:campaign_id/:tab?',
          element: (
            <ProtectedRoute>
              <SingleCampaignPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'rebates/:tab?',
          element: (
            <ProtectedRoute>
              <Rebates />
            </ProtectedRoute>
          ),
        },
        {
          path: 'rebates/offer/:offerId/:tab?',
          element: (
            <ProtectedRoute>
              <FeatureProtectedRoute featureFlag="enableRebates">
                <PageWithTitle title="Rebate Offer">
                  <RebateOfferDetails />
                </PageWithTitle>
              </FeatureProtectedRoute>
            </ProtectedRoute>
          ),
        },
      ],
    },
    {
      path: '/login',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Log-in">
              <Login />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/signup',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Sign-up">
              <Register />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/sso-login',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="SSO Login">
              <SSOLogin />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/auth/sso-callback',
      element: (
        <PageWithTitle title="SSO Authentication">
          <SSOCallback />
        </PageWithTitle>
      ),
    },
    {
      path: '/email-verified-confirmation',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Email Verified">
              <Verified />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/email-verification-failed',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Email Verification Failed">
              <Failed />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/reset-password',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Reset Password">
              <ResetPassword />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/password-reset-confirm/:uid/:token',
      element: <AuthLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Reset Password Confirmation">
              <ResetPasswordConfirm />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/terms-of-use',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Terms of Use">
              <Terms />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/privacy-policy',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Privacy Policy">
              <Privacy />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/subscription-agreement',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Subscription Agreement">
              <SubscriptionAgreement />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/rebate-platform-agreement',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Rebate Platform Agreement">
              <RebatePlatformAgreement />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/opt-out-form',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Opt Out Form">
              <OptOutForm />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/data-request-form',
      element: <CommonLayout />,
      children: [
        {
          path: '',
          element: (
            <PageWithTitle title="Data Request Form">
              <DataRequestForm />
            </PageWithTitle>
          ),
        },
      ],
    },
    {
      path: '/data-request-success',
      element: <CommonLayout />,
      children: [{ path: '', element: <DataRequestSuccess /> }],
    },
    {
      path: '/opt-out-success',
      element: <CommonLayout />,
      children: [{ path: '', element: <OptOutSuccess /> }],
    },
    {
      path: '/importer',
      element: (
        <FeatureProtectedRoute featureFlag="enableNewImportTool">
          <CommonLayout />
        </FeatureProtectedRoute>
      ),
      children: [
        { path: '', element: <ImportPreviewsPage /> },
        { path: ':import_number', element: <ImportPreviewPage /> },
      ],
    },
  ])

  return (
    <>
      <RouterProvider router={appRouter} />
      <IntercomMessenger />
    </>
  )
}

function RedirectRestaurants() {
  return <Navigate to={'/opportunities/commercial/restaurants-bars/'} />
}

function ProtectedRoute(props: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to={'/login'} />
  }

  return props.children
}

interface FeatureProtectedRouteProps {
  featureFlag: string
  children: React.ReactNode
}

function FeatureProtectedRoute({
  featureFlag,
  children,
}: FeatureProtectedRouteProps) {
  const featureFlags = featureFlagService()
  const isEnabled =
    featureFlags[featureFlag as keyof ReturnType<typeof featureFlagService>]

  if (featureFlags.isLoading) {
    return <LoadingScreen />
  }

  if (!isEnabled) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <PreferencesProvider>
        <Root />
        <ReactQueryDevtools />
        <ToastContainer />
      </PreferencesProvider>
    </QueryClientProvider>
  </AuthProvider>
)
